import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { buildApiUrl } from '../lib/api';
import { buildPostPath, formatPostDate, getReadTimeLabel, normalizePosts } from '../lib/posts';

export default function BlogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const { data: posts = [], isLoading: postsLoading, error: postsError } = useQuery({
    queryKey: ['blogPosts', currentPage, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        search: searchTerm,
      }).toString();
      const response = await fetch(buildApiUrl(`/blog?${params}`));

      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }

      const data = await response.json();

      return normalizePosts(Array.isArray(data) ? data : data.data || []);
    },
  });

  const filteredPosts = posts.filter(
    (post) =>
      post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleReadMore = (post) => {
    navigate(buildPostPath(post));
  };

  if (postsLoading) {
    return (
      <main className="page-shell">
        <div className="page-narrow surface-card flex min-h-[280px] items-center justify-center text-slate-600">
          Loading posts...
        </div>
      </main>
    );
  }

  if (postsError) {
    return (
      <main className="page-shell">
        <div className="page-narrow rounded-[28px] border border-rose-200 bg-rose-50 px-8 py-10 text-rose-700">
          Error: {postsError.message}
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <div className="page-width">
        <section className="page-header">
          <p className="eyebrow">Journal / notes / building in public</p>
        </section>

        <section className="page-narrow mt-12">
          <div className="surface-card-quiet p-5">
            <label htmlFor="blog-search" className="field-label">
              Search entries
            </label>
            <input
              id="blog-search"
              type="text"
              placeholder="Search blog posts..."
              className="field-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="mt-3 text-link"
              >
                Clear search
              </button>
            )}
          </div>
        </section>

        <div className="mx-auto mt-8 max-w-5xl space-y-6">
          {filteredPosts.map((post) => (
            <article key={post.id} className="surface-card px-6 py-7 md:px-8 md:py-9">
              <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
                <div className="max-w-2xl">
                  <h2 className="text-2xl font-medium leading-tight text-slate-900 md:text-[2rem]">
                    {post.title || 'Untitled Post'}
                  </h2>
                  <p className="mt-5 text-sm leading-8 text-slate-600">
                    {post.excerpt || (post.content ? `${post.content.substring(0, 120)}...` : 'No excerpt available')}
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-slate-500">
                    <span>{formatPostDate(post.createdAt)}</span>
                    <span className="text-slate-300">/</span>
                    <span>{getReadTimeLabel(post.content)}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleReadMore(post)}
                  disabled={!post.id}
                  className="text-link self-start disabled:text-slate-400"
                  aria-label={`Read full post: ${post.title}`}
                >
                  Read more
                </button>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && !postsLoading && (
          <div className="page-narrow mt-8 text-center">
            <div className="surface-card px-8 py-14">
              <p className="text-lg text-slate-500">No posts found matching your search.</p>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className="mt-5 text-link"
                >
                  Clear search and show all posts
                </button>
              )}
            </div>
          </div>
        )}

        {posts.length > 0 && (
          <div className="mt-12 flex items-center justify-center gap-6">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="secondary-button rounded-2xl px-5 py-3 disabled:translate-y-0 disabled:opacity-45"
            >
              Previous
            </button>
            <span className="text-sm text-slate-700">Page {currentPage}</span>
            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={posts.length < 10}
              className="secondary-button rounded-2xl px-5 py-3 disabled:translate-y-0 disabled:opacity-45"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
